interface MonitorCoreFieldsProps {
    name: string;
    description: string;
    action: string;
    lookbackDays: string;
    onNameChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    onActionChange: (value: string) => void;
    onLookbackDaysChange: (value: string) => void;
}

interface MonitorRelevanceRuleFieldsProps {
    relevanceMetric: string;
    relevanceOperator: string;
    relevanceThreshold: string;
    onRelevanceMetricChange: (value: string) => void;
    onRelevanceOperatorChange: (value: string) => void;
    onRelevanceThresholdChange: (value: string) => void;
}

export const MONITOR_TEXT = {
    invalidLookback: 'Please enter a valid number for Lookback days',
    genericSaveError: 'Failed to save strategy',
    genericUpdateError: 'Failed to update strategy',
};

export const monitorInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '4px',
    border: '1px solid var(--surface-glass-border)',
    background: 'var(--bg-app)',
    color: 'var(--text-primary)',
    boxSizing: 'border-box',
};

export const monitorLabelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 'bold',
    fontSize: '0.9rem',
};

export const monitorSecondaryButtonStyle: React.CSSProperties = {
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    border: '1px solid var(--surface-glass-border)',
    cursor: 'pointer',
    background: 'var(--bg-card)',
    color: 'var(--text-secondary)',
};

export function parseLookbackDays(value: string): number | null {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
}

export function MonitorCoreFields({
    name,
    description,
    action,
    lookbackDays,
    onNameChange,
    onDescriptionChange,
    onActionChange,
    onLookbackDaysChange,
}: MonitorCoreFieldsProps) {
    return (
        <>
            <div className="mb-md">
                <label style={monitorLabelStyle}>Strategy Name</label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                    style={monitorInputStyle}
                />
            </div>

            <div className="mb-md">
                <label style={monitorLabelStyle}>Description</label>
                <textarea
                    value={description}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    placeholder="What is this strategy for?"
                    style={{ ...monitorInputStyle, minHeight: '60px', fontFamily: 'inherit', resize: 'vertical' }}
                />
            </div>

            <div className="mb-md">
                <label style={monitorLabelStyle}>Action / Goal</label>
                <input
                    type="text"
                    value={action}
                    onChange={(e) => onActionChange(e.target.value)}
                    placeholder="e.g. Notify on High Volatility"
                    style={monitorInputStyle}
                />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ flex: 1 }}>
                    <label style={monitorLabelStyle}>Lookback (Days)</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={lookbackDays}
                        onChange={(e) => onLookbackDaysChange(e.target.value)}
                        style={monitorInputStyle}
                    />
                </div>
            </div>
        </>
    );
}

export function MonitorRelevanceRuleFields({
    relevanceMetric,
    relevanceOperator,
    relevanceThreshold,
    onRelevanceMetricChange,
    onRelevanceOperatorChange,
    onRelevanceThresholdChange,
}: MonitorRelevanceRuleFieldsProps) {
    return (
        <div style={{ padding: '1rem', border: '1px solid var(--accent-success)', borderRadius: '6px', backgroundColor: 'var(--accent-success-subtle)', marginBottom: '1.5rem' }}>
            <h4 style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--accent-success)' }}>🛡️ Relevance Rule (Optional)</h4>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                Only trigger alert if the 30-day statistics meet this criteria.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select
                    value={relevanceMetric}
                    onChange={(e) => onRelevanceMetricChange(e.target.value)}
                    style={{ flex: 1.5, padding: '0.4rem', borderRadius: '4px', border: '1px solid var(--surface-glass-border)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                >
                    <option value="">No relevance rule</option>
                    <option value="mean">30d Mean</option>
                    <option value="std">30d Std Dev</option>
                    <option value="min">30d Min</option>
                    <option value="max">30d Max</option>
                    <option value="count">30d Count</option>
                </select>

                {relevanceMetric && (
                    <>
                        <select
                            value={relevanceOperator}
                            onChange={(e) => onRelevanceOperatorChange(e.target.value)}
                            style={{ flex: 0.8, padding: '0.4rem', borderRadius: '4px', border: '1px solid var(--surface-glass-border)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                        >
                            <option value=">">&gt;</option>
                            <option value=">=">&gt;=</option>
                            <option value="<">&lt;</option>
                            <option value="<=">&lt;=</option>
                            <option value="=">=</option>
                        </select>

                        <input
                            type="text"
                            placeholder="Value"
                            value={relevanceThreshold}
                            onChange={(e) => onRelevanceThresholdChange(e.target.value)}
                            style={{ flex: 1.5, padding: '0.4rem', borderRadius: '4px', border: '1px solid var(--surface-glass-border)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '0' }}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
